import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IWorkHoursItem } from "../models/workTime/workHoursItem";
import { getCalculatedWorkHours } from "../services/workHoursService";
import { Base } from "../framework/base";
import { WorkShiftDtoBasic } from "../models/workShift/workShift";
import { DayBookingItemBeta } from "../models/dayBooking/dayBookingItemBeta";
import { getBasicWorkShifts } from "../services/workShiftService";
import { getDayBookingItems } from "../services/dayBookingService";
import { ApiResponse } from "../services/apiClient";

export interface EmployeeData {
    employeeIds: string[];
    workHours: IWorkHoursItem[];
    workShifts: WorkShiftDtoBasic[];
    dayBookings: DayBookingItemBeta[];
}

export interface EmployeeDataBySalaryPeriodId {
    [key: string]: EmployeeData;
}

export interface WorkTimeBetaState {
    employeeData: EmployeeDataBySalaryPeriodId;
    employeeDataLoading: boolean;
    workShiftsLoading: boolean;
    dayBookingsLoading: boolean;
}

export const initialState: WorkTimeBetaState = {
    employeeData: {},
    employeeDataLoading: false,
    workShiftsLoading: false,
    dayBookingsLoading: false,
};

// Include shifts which are inside the date range by effective date
// Or have a split at attribute inside the date range
const getFilteredWorkShifts = (
    res: ApiResponse<WorkShiftDtoBasic[]>,
    startDate: string,
    endDate: string
) =>
    (res?.data ?? []).filter(
        (s) =>
            (s.effectiveDate >= startDate && s.effectiveDate <= endDate) ||
            (!!s.splitAt && s.splitAt >= startDate && s.splitAt <= endDate)
    );

export const workTimeListSlice = createSlice({
    name: "workTimeList",
    initialState,
    reducers: {
        setEmployeeIds: (
            state,
            action: PayloadAction<{
                salaryPeriodId: string;
                employeeIds: string[];
            }>
        ) => {
            const existing = (state.employeeData[
                action.payload.salaryPeriodId
            ] ??= {} as EmployeeData);
            const existingEmployeeIds = existing?.employeeIds ?? [];

            state.employeeData[action.payload.salaryPeriodId].employeeIds =
                Base.getUniqueStringItems([
                    ...action.payload.employeeIds,
                    ...existingEmployeeIds,
                ]);
            state.employeeDataLoading = false;
        },
        workHoursReceived: (
            state,
            action: PayloadAction<{
                salaryPeriodId: string;
                employeeIds: string[];
                workHours: IWorkHoursItem[];
            }>
        ) => {
            const existing = (state.employeeData[
                action.payload.salaryPeriodId
            ] ??= {} as EmployeeData);
            const existingHours = existing?.workHours ?? [];

            state.employeeData[action.payload.salaryPeriodId].workHours = [
                ...existingHours.filter(
                    (h) => !action.payload.employeeIds.includes(h.employeeId)
                ),
                ...action.payload.workHours,
            ];
        },
        workShiftsReceived: (
            state,
            action: PayloadAction<{
                salaryPeriodId: string;
                workShifts: WorkShiftDtoBasic[];
                employeeIds: string[];
            }>
        ) => {
            const existing = (state.employeeData[
                action.payload.salaryPeriodId
            ] ??= {} as EmployeeData);
            const existingShifts = existing?.workShifts ?? [];

            state.employeeData[action.payload.salaryPeriodId].workShifts = [
                ...existingShifts.filter(
                    (h) => !action.payload.employeeIds.includes(h.employeeId)
                ),
                ...action.payload.workShifts,
            ];
            state.workShiftsLoading = false;
        },
        dayBookingsReceived: (
            state,
            action: PayloadAction<{
                salaryPeriodId: string;
                dayBookings: DayBookingItemBeta[];
                employeeIds: string[];
            }>
        ) => {
            const existing = (state.employeeData[
                action.payload.salaryPeriodId
            ] ??= {} as EmployeeData);
            const existingShifts = existing?.dayBookings ?? [];

            state.employeeData[action.payload.salaryPeriodId].dayBookings = [
                ...existingShifts.filter(
                    (h) => !action.payload.employeeIds.includes(h.employeeId)
                ),
                ...action.payload.dayBookings,
            ];
            state.dayBookingsLoading = false;
        },
        setEmployeeDataLoading: (state, action: PayloadAction<boolean>) => {
            state.employeeDataLoading = action.payload;
        },
        setWorkShiftsLoading: (state, action: PayloadAction<boolean>) => {
            state.workShiftsLoading = action.payload;
        },
        setDayBookingsLoading: (state, action: PayloadAction<boolean>) => {
            state.dayBookingsLoading = action.payload;
        },
    },
});

export const {
    setEmployeeIds,
    workHoursReceived,
    workShiftsReceived,
    dayBookingsReceived,
    setEmployeeDataLoading,
    setWorkShiftsLoading,
    setDayBookingsLoading,
} = workTimeListSlice.actions;

export const getEmployeeData = createAsyncThunk(
    "workTimeList/employeeData",
    async(
        {
            employeeGroupId,
            employeeIds,
            salaryPeriodId,
            startDate,
            endDate,
            calculateFillingHours,
        }: {
            employeeGroupId: string;
            employeeIds: string[];
            salaryPeriodId: string;
            startDate: string;
            endDate: string;
            calculateFillingHours: boolean;
        },
        thunkApi
    ) => {
        thunkApi.dispatch(setEmployeeDataLoading(true));

        const promises = [
            getCalculatedWorkHours(
                employeeGroupId,
                employeeIds,
                salaryPeriodId,
                calculateFillingHours,
                thunkApi.signal
            ).then((workHours) =>
                thunkApi.dispatch(
                    workHoursReceived({
                        salaryPeriodId,
                        workHours,
                        employeeIds,
                    })
                )
            ),
            getBasicWorkShifts(
                employeeIds,
                Base.dayjsToJsonDate(Base.dateAddDays(startDate, -1)),
                Base.dayjsToJsonDate(Base.dateAddDays(endDate, 1)),
                true,
                thunkApi.signal
            ).then((res) =>
                thunkApi.dispatch(
                    workShiftsReceived({
                        salaryPeriodId,
                        workShifts: getFilteredWorkShifts(
                            res,
                            startDate,
                            endDate
                        ),
                        employeeIds,
                    })
                )
            ),
            getDayBookingItems(
                startDate,
                endDate,
                employeeIds,
                thunkApi.signal
            ).then((dayBookings) =>
                thunkApi.dispatch(
                    dayBookingsReceived({
                        salaryPeriodId,
                        dayBookings,
                        employeeIds,
                    })
                )
            ),
        ];

        await Promise.all(promises);

        thunkApi.dispatch(
            setEmployeeIds({
                salaryPeriodId,
                employeeIds,
            })
        );
    }
);

export const loadWorkShifts = createAsyncThunk(
    "workTimeList/workShifts",
    async(
        {
            employeeIds,
            salaryPeriodId,
            startDate,
            endDate,
        }: {
            employeeIds: string[];
            salaryPeriodId: string;
            startDate: string;
            endDate: string;
            preserveExisting: boolean;
        },
        thunkApi
    ) => {
        thunkApi.dispatch(setWorkShiftsLoading(true));

        const res = await getBasicWorkShifts(
            employeeIds,
            Base.dayjsToJsonDate(Base.dateAddDays(startDate, -1)),
            Base.dayjsToJsonDate(Base.dateAddDays(endDate, 1)),
            true,
            thunkApi.signal
        );

        thunkApi.dispatch(
            workShiftsReceived({
                salaryPeriodId,
                workShifts: getFilteredWorkShifts(res, startDate, endDate),
                employeeIds,
            })
        );
    }
);

export const loadDayBookings = createAsyncThunk(
    "workTimeList/dayBookings",
    async(
        {
            employeeIds,
            salaryPeriodId,
            startDate,
            endDate,
        }: {
            employeeIds: string[];
            salaryPeriodId: string;
            startDate: string;
            endDate: string;
            preserveExisting: boolean;
        },
        thunkApi
    ) => {
        thunkApi.dispatch(setDayBookingsLoading(true));

        const dayBookings = await getDayBookingItems(
            startDate,
            endDate,
            employeeIds,
            thunkApi.signal
        );

        thunkApi.dispatch(
            dayBookingsReceived({
                salaryPeriodId,
                dayBookings,
                employeeIds,
            })
        );
    }
);

export const workTimeListReducer = workTimeListSlice.reducer;
